import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '@mui/joy';
import _ from 'lodash';
import { InstalledBaseNodeEventContext } from '../../graphql/queries/installedBaseNodeEventContext';
import { AssetAssetLocation } from '../../graphql/queries/assetAssetLocation';
import { ManualEventType } from '../../codegen/graphql';
import { filterAvailableEvents } from '../../utils/filterAvailableEvents';
import { useEventTypeLabels } from '../../utils/eventTypeLabels';
import { getEventTypeIcon } from '../../utils/eventTypeIcons';
import CreateInstalledBaseEventModal from '../CreateInstalledBaseEventModal';

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
}

const AssetEventActionShortcuts = ({ assetLocationId, installedBaseNodeId }: Props) => {
    const eventTypeLabels = useEventTypeLabels();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEventType, setSelectedEventType] = useState<ManualEventType | undefined>(undefined);

    const assetQuery = useQuery(AssetAssetLocation, {
        variables: { id: assetLocationId },
    });
    const assetId = _.get(assetQuery, 'data.assetLocation.asset.id', null);

    const eventContextQuery = useQuery(InstalledBaseNodeEventContext, {
        variables: { id: installedBaseNodeId },
        skip: !installedBaseNodeId,
    });
    const availableEvents: ManualEventType[] = filterAvailableEvents(
        _.get(eventContextQuery, 'data.installedBaseNode.availableInstalledBaseEvents', []),
        _.get(eventContextQuery, 'data.installedBaseNode.assetLocation.isInstalled'),
    );

    if (availableEvents.length === 0) return null;

    const handleClick = (eventType: ManualEventType) => {
        setSelectedEventType(eventType);
        setModalOpen(true);
    };

    return (
        <>
            {availableEvents.map((eventType) => (
                <Button
                    key={eventType}
                    variant="outlined"
                    color="neutral"
                    startDecorator={getEventTypeIcon(eventType, { fontSize: 20 })}
                    onClick={() => handleClick(eventType)}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {eventTypeLabels[eventType]}
                </Button>
            ))}

            {modalOpen && assetId &&
                <CreateInstalledBaseEventModal
                    assetId={assetId}
                    assetLocationId={assetLocationId}
                    installedBaseNodeId={installedBaseNodeId}
                    open={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedEventType(undefined);
                    }}
                    initialEventType={selectedEventType}
                />
            }
        </>
    );
};

export default AssetEventActionShortcuts;
